import { addDatadogRumAction } from 'vendor/datadog-rum';
/**
 * Plan-board funnel telemetry.
 *
 * Every optimistic flow this feature added can silently diverge from server
 * truth (a verify-mismatch background refetch, a rollback, a conflict modal).
 * Backend anomaly detection sees deviations in its logs; the client can't —
 * so each flow emits a structured RUM action at every step, and the DEVIATION
 * branches get their own step names. Aggregating these server-side (Datadog)
 * turns "expected path vs actual" into a queryable funnel: a healthy flow is
 * `start -> optimistic -> verified`; a spike in `reconcile` / `error` /
 * `conflict` is the anomaly.
 *
 * Fire-and-forget, RUM-init-safe (the sink lazily resolves the async RUM
 * chunk and no-ops until it's ready), so a call is safe anywhere including
 * the collection write layer.
 */ const emit = (event, context)=>{
    addDatadogRumAction(event, context);
};
export const planFunnel = {
    // 1 — Cross-phase move (drag a task/sprint into another phase).
    moveStart: (ctx)=>emit('plan.move.start', ctx),
    moveOptimistic: (ctx)=>emit('plan.move.optimistic', ctx),
    moveVerified: (ctx)=>emit('plan.move.verified', ctx),
    /** DEVIATION: server resolved a different order/phase — background refetch. */ moveReconcile: (ctx)=>emit('plan.move.reconcile', ctx),
    /** DEVIATION: task dates fall outside the destination phase → conflict modal. */ moveDateConflict: (ctx)=>emit('plan.move.date_conflict', ctx),
    moveConflictConfirm: (ctx)=>emit('plan.move.conflict_confirm', ctx),
    moveConflictCancel: (ctx)=>emit('plan.move.conflict_cancel', ctx),
    /** DEVIATION: move rejected server-side → rollback refetch. */ moveError: (ctx)=>emit('plan.move.error', ctx),
    // 2 — In-lane reorder (drag within the same phase).
    reorderStart: (ctx)=>emit('plan.reorder.start', ctx),
    reorderVerified: (ctx)=>emit('plan.reorder.verified', ctx),
    /** DEVIATION: optimistic order != server resolved position. */ reorderReconcile: (ctx)=>emit('plan.reorder.reconcile', ctx),
    reorderError: (ctx)=>emit('plan.reorder.error', ctx),
    // 3 — Task status change (card pill → phase progress counters).
    taskStatusChange: (ctx)=>emit('plan.task_status.change', ctx),
    taskStatusError: (ctx)=>emit('plan.task_status.error', ctx),
    // 4 — Phase status change (header pill, board-only optimistic patch).
    phaseStatusChange: (ctx)=>emit('plan.phase_status.change', ctx),
    phaseStatusError: (ctx)=>emit('plan.phase_status.error', ctx),
    // 5 — Phase edit modal (name / dates / budget / privacy).
    phaseEditSave: (ctx)=>emit('plan.phase_edit.save', ctx),
    /** Phase marked private → its loaded tasks cascade to private. */ phaseEditPrivacyCascade: (ctx)=>emit('plan.phase_edit.privacy_cascade', ctx),
    /** DEVIATION: date change hits dependent entities → dependency modal. */ phaseEditDependencyConflict: (ctx)=>emit('plan.phase_edit.dependency_conflict', ctx),
    phaseEditError: (ctx)=>emit('plan.phase_edit.error', ctx),
    // 6 — Budget filter (server-side lane filtering).
    budgetFilterApply: (ctx)=>emit('plan.budget_filter.apply', ctx),
    budgetFilterError: (ctx)=>emit('plan.budget_filter.error', ctx),
    // 7 — Lane pagination (infinite scroll).
    pageLoadMore: (ctx)=>emit('plan.pagination.load_more', ctx),
    /** DEVIATION: load-more fired but the page brought no new tokens. */ pageEmpty: (ctx)=>emit('plan.pagination.empty', ctx),
    pageError: (ctx)=>emit('plan.pagination.error', ctx),
    // 8 — Phase rearrange (manual-sequencing modal — optimistic reorder + close).
    phaseRearrangeStart: (ctx)=>emit('plan.phase_rearrange.start', ctx),
    phaseRearrangeOptimistic: (ctx)=>emit('plan.phase_rearrange.optimistic', ctx),
    phaseRearrangeVerified: (ctx)=>emit('plan.phase_rearrange.verified', ctx),
    /** DEVIATION: server resolved a different phase order → background refetch. */ phaseRearrangeReconcile: (ctx)=>emit('plan.phase_rearrange.reconcile', ctx),
    /** DEVIATION: reorder rejected server-side → rollback to prior order. */ phaseRearrangeError: (ctx)=>emit('plan.phase_rearrange.error', ctx)
};
